import { useState } from 'react'
import type { TagEntry } from '@/features/gate/commands'
import { Btn } from '@/features/gate/v1/kit'
import { LiveScanner, Refused, TypeCodeSheet } from '@/features/gate/v1/scan'
import { tr } from '@/features/gate/v1/text'
import { matchTypedCode } from './logic'

/**
 * The godown boards' scanner: the live camera, नंबर लिखें for the number printed under a tag,
 * and the red line when a code is refused. `onCode` gets the whole code (a typed number is
 * matched against `payloads`) and how it was entered, and returns the refusal to show, or null.
 */
export function TagScanner({
  hint,
  payloads,
  onCode,
  paused = false,
  side,
  kind = 'tag',
}: {
  hint: string
  /** Codes a typed number may name (the trip's list, the bags here). */
  payloads: readonly string[]
  /** `entry` says whether the code was scanned or typed (नंबर लिखें). */
  onCode: (code: string, entry: TagEntry) => string | null
  /** A sheet or full-screen card is up: keep the camera off. */
  paused?: boolean
  /** A second button beside नंबर लिखें (बोरी हटाएँ). */
  side?: { icon: 'undo' | 'x'; label: string; onClick: () => void }
  /** A place label (bay or spot): the number sheet says so. */
  kind?: 'tag' | 'place'
}) {
  const [err, setErr] = useState<string | null>(null)
  const [typing, setTyping] = useState(false)
  const check = (raw: string, entry: TagEntry): string | null => {
    const code = entry === 'TYPED' ? matchTypedCode(payloads, raw) : raw.trim().toUpperCase()
    if (!code) return tr(kind === 'place' ? 'store.v1.typePlaceNotFound' : 'store.v1.typeNotFound')
    return onCode(code, entry)
  }
  return (
    <>
      {typing && (
        <TypeCodeSheet
          title={tr(kind === 'place' ? 'store.v1.typePlaceTitle' : 'store.v1.typeTitle')}
          check={(raw, entry) => {
            const e = check(raw, entry)
            setErr(null)
            return e
          }}
          onClose={() => setTyping(false)}
        />
      )}
      {!paused && !typing && (
        <LiveScanner icon="tag" hint={hint} minHeight={170} check={check} onError={setErr} />
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <Btn icon="keypad" onClick={() => setTyping(true)}>
          {tr('gate.v1.tag.type')}
        </Btn>
        {side && (
          <Btn icon={side.icon} onClick={side.onClick}>
            {side.label}
          </Btn>
        )}
      </div>
      {err && <Refused text={err} />}
    </>
  )
}
